import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  SUPPORT_CHUNK,
  SUPPORT_MESSAGE_WS_EVENT,
} from '@app/api/Support/const';
import {
  mergeSupportMessageEvent,
  parseSupportMessageEvent,
} from '@app/api/Support/supportMessageEvent';
import {
  SupportEndpoints as Endpoints,
  type GetSupportMessageFileRequest,
  type GetSupportMessageFileResponse,
  type GetSupportMessagesRequest,
  type GetSupportMessagesResponse,
  type SendMessageToSupportRequest,
  type SendMessageToSupportResponse,
} from '@app/api/Support/types';
import {
  addWsEventListener,
  createWSConnection,
  subscribeToWsReconnect,
} from '@app/utils/createWSConnection';

const SUPPORT_MESSAGES_TAG = 'SupportMessages';

export const supportApi = createApi({
  reducerPath: 'supportApi',
  baseQuery: createApiBaseQuery(SUPPORT_CHUNK),
  tagTypes: [SUPPORT_MESSAGES_TAG],
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetSupportMessages]: query<
      GetSupportMessagesResponse,
      GetSupportMessagesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSupportMessages,
        method: Methods.GET,
        params,
      }),
      providesTags: [SUPPORT_MESSAGES_TAG],
      // Пока запись кеша жива (окно чата открыто), слушаем OnSupportMessage и
      // вливаем новое сообщение прямо в кеш — без повторного запроса.
      onCacheEntryAdded: async (
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
      ) => {
        // Пока соединения не было, сообщения могли пройти мимо — перечитываем.
        const unsubscribers = [
          subscribeToWsReconnect(() => {
            dispatch(supportApi.util.invalidateTags([SUPPORT_MESSAGES_TAG]));
          }),
        ];

        try {
          await cacheDataLoaded;

          // addWsEventListener вешает обработчик на хаб, только если
          // соединение уже создано, — поэтому сначала дожидаемся его.
          await createWSConnection(() => {
            unsubscribers.push(
              addWsEventListener(SUPPORT_MESSAGE_WS_EVENT, (raw: unknown) => {
                const incoming = parseSupportMessageEvent(raw);

                if (incoming) {
                  updateCachedData((draft) =>
                    mergeSupportMessageEvent(draft, incoming)
                  );
                }
              })
            );
          });
        } catch {
          // Запись удалили раньше, чем пришли данные, — слушать нечего
        }

        await cacheEntryRemoved;

        unsubscribers.forEach((unsubscribe) => unsubscribe());
      },
    }),
    [Endpoints.SendMessageToSupport]: mutation<
      SendMessageToSupportResponse,
      SendMessageToSupportRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SendMessageToSupport,
        method: Methods.POST,
        params,
        data,
      }),
      invalidatesTags: [SUPPORT_MESSAGES_TAG],
    }),
    [Endpoints.GetSupportMessageFile]: query<
      GetSupportMessageFileResponse,
      GetSupportMessageFileRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSupportMessageFile,
        method: Methods.GET,
        params,
      }),
      // Содержимое нужно один раз — для скачивания, в кеше его не держим
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetSupportMessagesQuery,
  useSendMessageToSupportMutation,
  useLazyGetSupportMessageFileQuery,
} = supportApi;
